/**
 * 布局视口工具：PC/移动端工作区、横竖屏与弹窗高度均基于 layout viewport，
 * 软键盘弹起时 visualViewport 高度会变小，若直接用它会导致布局翻转、弹窗收缩并丢失输入焦点。
 */

/** 平板工作区最大短边（px）；短边超过此值视为 PC 桌面工作区。 */
export const WORKSPACE_TABLET_MAX_SHORT_SIDE = 1180

export type LayoutOrientation = 'portrait' | 'landscape'

/** 触屏工作区 UI 朝向缓存；软键盘挤压 layout viewport 时仍沿用最近一次可靠朝向。 */
let cachedMobileUiOrientation: LayoutOrientation | null = null

/** 当前焦点是否在可编辑控件上。 */
function isEditableElementFocused(): boolean {
  if (typeof document === 'undefined') return false
  const el = document.activeElement
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return !el.disabled && !el.readOnly
  }
  if (el instanceof HTMLSelectElement) {
    return !el.disabled
  }
  if (el instanceof HTMLElement) {
    return el.isContentEditable
  }
  return false
}

/** 读取布局视口尺寸（window.innerWidth/innerHeight，不受软键盘挤压）。 */
export function readLayoutViewportSize(): { width: number; height: number } {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 }
  }
  return {
    width: Math.round(window.innerWidth),
    height: Math.round(window.innerHeight),
  }
}

/** 是否主输入为触屏/粗指针设备。 */
export function isCoarsePointerDevice(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return true
  return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1
}

/**
 * 当前视口短边是否在平板工作区阈值内。
 * @param maxShortSide 平板工作区最大短边，默认 {@link WORKSPACE_TABLET_MAX_SHORT_SIDE}。
 */
export function isTabletWorkspaceViewport(maxShortSide = WORKSPACE_TABLET_MAX_SHORT_SIDE): boolean {
  if (typeof window === 'undefined') return false
  const { width, height } = readLayoutViewportSize()
  return Math.min(width, height) <= maxShortSide
}

/**
 * 是否移动端/平板工作区（短边在平板阈值内且主输入为触屏）。
 * 与 {@link isDesktopWorkspace} 互斥，用于资源触屏交互等。
 */
export function isMobileWorkspace(maxShortSide = WORKSPACE_TABLET_MAX_SHORT_SIDE): boolean {
  return isTabletWorkspaceViewport(maxShortSide) && isCoarsePointerDevice()
}

/** 是否 PC 桌面工作区（与 {@link isMobileWorkspace} 互斥）。 */
export function isDesktopWorkspace(maxShortSide = WORKSPACE_TABLET_MAX_SHORT_SIDE): boolean {
  return !isMobileWorkspace(maxShortSide)
}

/**
 * 当前布局视口物理朝向（仅比较宽高，与是否启用竖屏 UI 分支无关）。
 */
export function readLayoutOrientation(): LayoutOrientation {
  const mobileOrientation = readMobileScreenOrientation()
  if (mobileOrientation) return mobileOrientation
  const { width, height } = readLayoutViewportSize()
  return width < height ? 'portrait' : 'landscape'
}

/**
 * 读取设备物理朝向（Screen Orientation / screen 尺寸，不受软键盘挤压 layout viewport 影响）。
 */
export function readPhysicalScreenOrientation(): LayoutOrientation | null {
  if (typeof window === 'undefined') return null

  const type = String(window.screen?.orientation?.type ?? '')
  if (type.includes('portrait')) return 'portrait'
  if (type.includes('landscape')) return 'landscape'

  const angle = (window as Window & { orientation?: number }).orientation
  if (typeof angle === 'number' && Number.isFinite(angle)) {
    return Math.abs(angle) === 90 ? 'landscape' : 'portrait'
  }

  const sw = window.screen?.width ?? 0
  const sh = window.screen?.height ?? 0
  if (sw > 0 && sh > 0 && sw !== sh) {
    return sw < sh ? 'portrait' : 'landscape'
  }

  return null
}

/**
 * 触屏平板工作区下的 UI 朝向；不依赖 `(orientation: …)` 媒体查询（键盘弹出时可能误判）。
 * 软键盘弹起或输入聚焦时禁止用 layout viewport 宽高比刷新缓存。
 */
function readMobileScreenOrientation(): LayoutOrientation | null {
  if (typeof window === 'undefined') return null
  if (!isCoarsePointerDevice() || !isTabletWorkspaceViewport()) {
    cachedMobileUiOrientation = null
    return null
  }

  const physical = readPhysicalScreenOrientation()
  if (physical) {
    cachedMobileUiOrientation = physical
    return physical
  }

  if (!isMobileSoftKeyboardActive()) {
    const { width, height } = readLayoutViewportSize()
    if (width > 0 && height > 0 && width !== height) {
      cachedMobileUiOrientation = width < height ? 'portrait' : 'landscape'
    }
  }

  return cachedMobileUiOrientation
}

/**
 * 是否启用竖屏专用 UI 分支：宽度不超过阈值且为竖屏。
 * 触屏设备用物理朝向；PC 仍用 layout viewport 宽高比。
 */
export function isPortraitLayoutViewport(maxWidth = WORKSPACE_TABLET_MAX_SHORT_SIDE): boolean {
  const { width, height } = readLayoutViewportSize()
  if (width > maxWidth) return false
  const mobileOrientation = readMobileScreenOrientation()
  if (mobileOrientation === 'portrait') return true
  if (mobileOrientation === 'landscape') return false
  return width < height
}

/**
 * 解析竖屏 UI 分支；软键盘弹起时若当前已在竖屏分支，禁止切到横屏（避免 DOM 切换导致输入失焦）。
 * @param maxWidth 平板工作区最大宽度阈值。
 * @param currentPortrait 当前是否处于竖屏 UI 分支。
 */
export function resolvePortraitLayoutViewport(
  maxWidth = WORKSPACE_TABLET_MAX_SHORT_SIDE,
  currentPortrait = false,
): boolean {
  if (!isMobileWorkspace(maxWidth)) {
    return isPortraitLayoutViewport(maxWidth)
  }

  const { width } = readLayoutViewportSize()
  if (width > maxWidth) return false

  const physical = readPhysicalScreenOrientation()
  if (physical === 'portrait') return true
  if (physical === 'landscape') return false

  if (currentPortrait && (isMobileSoftKeyboardActive() || cachedMobileUiOrientation === 'portrait')) {
    return true
  }

  return isPortraitLayoutViewport(maxWidth)
}

/** 软键盘是否可能已弹起，或移动端输入框已聚焦（`interactive-widget=overlays-content` 时视口可能不收缩）。 */
export function isMobileSoftKeyboardActive(threshold = 80): boolean {
  if (typeof window === 'undefined') return false
  if (!isMobileWorkspace()) return false
  if (isMobileKeyboardLikelyOpen(threshold)) return true
  return isEditableElementFocused()
}

/**
 * 是否启用横屏平板 UI 分支：宽度不超过阈值且宽大于等于高。
 * 宽于阈值的横屏平板（如 iPad 横屏）不在此分支内，仍走 PC 横栏布局。
 */
export function isLandscapeLayoutViewport(maxWidth = WORKSPACE_TABLET_MAX_SHORT_SIDE): boolean {
  const { width, height } = readLayoutViewportSize()
  if (width > maxWidth) return false
  const mobileOrientation = readMobileScreenOrientation()
  if (mobileOrientation === 'landscape') return true
  if (mobileOrientation === 'portrait') return false
  return width >= height
}

/** 判断软键盘是否可能已弹起（依据 visualViewport 与布局视口高度差）。 */
export function isMobileKeyboardLikelyOpen(threshold = 80): boolean {
  if (typeof window === 'undefined') return false
  const viewport = window.visualViewport
  if (!viewport) return false
  const layoutHeight = window.innerHeight
  const gap = Math.max(0, layoutHeight - viewport.height - (viewport.offsetTop ?? 0))
  return gap > threshold
}

/**
 * 读取用于全屏弹窗等内容区约束的有效视口高度。
 * 软键盘弹起时保持布局视口高度，避免弹窗随 visualViewport 收缩。
 */
export function readStableViewportHeight(): number {
  const { height: layoutHeight } = readLayoutViewportSize()
  if (isMobileSoftKeyboardActive()) {
    return layoutHeight
  }
  if (typeof window === 'undefined') return 0
  return Math.round(window.visualViewport?.height ?? layoutHeight)
}
